import React, { FC } from 'react';

import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';

import dayjs from 'services/dayjs';

import { Typography, Countdown, Grid, Column } from 'components';
import { Header, Description, LearnMoreLink } from './styled';
import { isTabletOrSmaller } from 'helpers';

type HeadersType = {
    campaignEnds: string;
    onClick?: () => void;
};

export const ExistingUsers: FC<HeadersType> = ({ campaignEnds, onClick }) => {
    const ends = dayjs(campaignEnds);

    return (
        <React.Fragment>
            <Typography
                variant="h4"
                color="premiumPlus"
                transform="uppercase"
                spaceBottom="small"
            >
                <Trans>Our best deal of the year</Trans>
            </Typography>
            <Header
                variant="h2"
                color="white"
                transform="uppercase"
                fontWeight={500}
            >
                <Trans>Black Friday megasale</Trans>
            </Header>
            <Header
                variant="h2"
                color="brand"
                transform="uppercase"
                fontWeight={500}
            >
                <Trans>50% off</Trans>
            </Header>

            <Countdown
                from={campaignEnds}
                fontSize={isTabletOrSmaller() ? '1.25rem' : '2rem'}
            />

            <Description color="premiumPlus" variant="bodyl">
                {/* subtract one day to show the last day of campaign */}
                {i18n._(t`Ends ${ends.subtract(1, 'day').format('MMMM D')}`)}
            </Description>

            <Description variant="bodyl" color="white">
                <Trans>
                    Imagine unlocking everything Premium+ has to offer. Now you
                    can! Get a whole year of playing every instrument and song
                    you like for half the price.
                </Trans>
            </Description>

            <LearnMoreLink onClick={onClick}>
                <Trans>Learn more</Trans>
            </LearnMoreLink>
        </React.Fragment>
    );
};

export const NewUsers: FC<HeadersType> = ({ campaignEnds, onClick }) => {
    const ends = dayjs(campaignEnds);

    return (
        <React.Fragment>
            <Typography
                variant="h4"
                color="brand"
                transform="uppercase"
                spaceBottom="small"
            >
                <Trans>Black Friday megasale</Trans>
            </Typography>
            <Header
                variant="h2"
                color="white"
                transform="uppercase"
                fontWeight={500}
                $color="brand"
            >
                <Trans>
                    Start your musical journey today <span>50% OFF</span>
                </Trans>
            </Header>

            <Description color="premiumPlus" variant="bodyl">
                {/* subtract one day to show the last day of campaign */}
                {i18n._(
                    t`Limited Time Offer: Ends ${ends
                        .subtract(1, 'day')
                        .format('MMMM D')}`,
                )}
            </Description>

            <Description variant="bodyl" color="white">
                <Trans>
                    Learn to play the music you love with unlimited access to
                    thousands of songs and lessons. Join 1.2 million music
                    lovers and start your musical journey today!
                </Trans>
            </Description>

            <LearnMoreLink onClick={onClick}>
                <Trans>Learn more</Trans>
            </LearnMoreLink>
        </React.Fragment>
    );
};

export const FamilyOnlyUsers: FC<HeadersType> = ({ campaignEnds, onClick }) => {
    const ends = dayjs(campaignEnds);

    return (
        <React.Fragment>
            <Typography
                variant="h4"
                color="premiumPlus"
                transform="uppercase"
                spaceBottom="small"
            >
                <Trans>Our best deal of the year</Trans>
            </Typography>
            <Header
                variant="h2"
                color="white"
                transform="uppercase"
                fontWeight={500}
            >
                <Trans>Black Friday megasale</Trans>
            </Header>
            <Header
                variant="h2"
                color="brand"
                transform="uppercase"
                fontWeight={500}
            >
                <Trans>50% off</Trans>
            </Header>

            <Countdown
                from={campaignEnds}
                fontSize={isTabletOrSmaller() ? '1.25rem' : '2rem'}
            />

            <Description color="premiumPlus" variant="bodyl">
                {/* subtract one day to show the last day of campaign */}
                {i18n._(t`Ends ${ends.subtract(1, 'day').format('MMMM D')}`)}
            </Description>

            <Description variant="bodyl" color="white">
                <Trans>
                    Spend less, play more - as a family! With our most
                    value-packed membership ever, you can discover everything
                    Premium+ has to offer together with 3 family members.
                </Trans>
            </Description>

            <LearnMoreLink onClick={onClick}>
                <Trans>Learn more</Trans>
            </LearnMoreLink>
        </React.Fragment>
    );
};

export const ExistingUsersConversion: FC<
    HeadersType & React.HTMLAttributes<HTMLDivElement>
> = ({ campaignEnds }) => {
    return (
        <React.Fragment>
            <Grid alignCenter>
                <Column cols={12} laptop={9} desktop={8}>
                    <Typography
                        spaceTop="xgiant"
                        align="center"
                        variant="h2"
                        color="white"
                        transform="uppercase"
                        fontWeight={500}
                    >
                        <Trans>Black Friday megasale</Trans>
                    </Typography>
                    <Typography
                        variant="h2"
                        color="brand"
                        transform="uppercase"
                        fontWeight={500}
                        align="center"
                        spaceBottom="large"
                    >
                        <Trans>50% off</Trans>
                    </Typography>

                    <Typography align="center" color="white" variant="bodyl">
                        <Trans>Unlock everything Yousician has to offer</Trans>
                    </Typography>

                    <Typography
                        align="center"
                        color="premiumPlus"
                        variant="bodyl"
                        spaceBottom="large"
                    >
                        <Trans>Ends in</Trans>{' '}
                        <Countdown
                            from={campaignEnds}
                            showClock={false}
                            color="premiumPlus"
                            fontSize="inherit"
                        />
                    </Typography>
                </Column>
            </Grid>
        </React.Fragment>
    );
};

export const NewUsersConversion: FC<
    HeadersType & React.HTMLAttributes<HTMLDivElement>
> = ({ campaignEnds }) => {
    return (
        <React.Fragment>
            <Grid alignCenter>
                <Column cols={12} laptop={9} desktop={8}>
                    <Typography
                        spaceTop="xgiant"
                        align="center"
                        variant="h4"
                        color="brand"
                        transform="uppercase"
                        spaceBottom="small"
                    >
                        <Trans>Black Friday megasale</Trans>
                    </Typography>
                    <Header
                        align="center"
                        variant="h2"
                        color="white"
                        transform="uppercase"
                        fontWeight={500}
                        $color="brand"
                    >
                        <Trans>
                            Start your musical journey today{' '}
                            <span>50% OFF</span>
                        </Trans>
                    </Header>
                    <Typography
                        align="center"
                        color="premiumPlus"
                        variant="bodyl"
                        spaceTop="large"
                        spaceBottom="xgiant"
                    >
                        <Trans>Ends in</Trans>{' '}
                        <Countdown
                            from={campaignEnds}
                            showClock={false}
                            color="premiumPlus"
                            fontSize="inherit"
                        />
                    </Typography>
                </Column>
            </Grid>
        </React.Fragment>
    );
};

export const FamilyOnlyUsersConversion: FC<
    HeadersType & React.HTMLAttributes<HTMLDivElement>
> = ({ campaignEnds }) => {
    return (
        <React.Fragment>
            <Grid alignCenter>
                <Column cols={12} laptop={9} desktop={8}>
                    <Typography
                        spaceTop="xgiant"
                        align="center"
                        variant="h2"
                        color="white"
                        transform="uppercase"
                        fontWeight={500}
                    >
                        <Trans>Black Friday megasale</Trans>
                    </Typography>
                    <Typography
                        variant="h2"
                        color="brand"
                        transform="uppercase"
                        fontWeight={500}
                        align="center"
                        spaceBottom="large"
                    >
                        <Trans>50% off</Trans>
                    </Typography>

                    <Typography align="center" color="white" variant="bodyl">
                        <Trans>Discover music as a family</Trans>
                    </Typography>

                    <Typography
                        align="center"
                        color="premiumPlus"
                        variant="bodyl"
                        spaceBottom="large"
                    >
                        <Trans>Ends in</Trans>{' '}
                        <Countdown
                            from={campaignEnds}
                            showClock={false}
                            color="premiumPlus"
                            fontSize="inherit"
                        />
                    </Typography>
                </Column>
            </Grid>
        </React.Fragment>
    );
};
